import {
  getTeamMemberListData,
  _getTMDataById,
  _getTMSelectItemDataById,
} from '../teamMembers/mockData'
import {
  getProjectListData,
  _getProjSelectItemDataById,
} from '../projects/mockData'
import {
  getLocationListData,
  _getLSelectItemDataById,
} from '../locations/mockData'
import {
  _getSSelectItemDataById,
  getSupplierListData,
} from '../suppliers/mockData'
import { getTsdListData, _getTSelectItemDataById } from '../tsds/mockData'
import {
  _getCSelectItemDataById,
  getCompanyListData,
} from '../companies/mockData'
import {
  _getCCTDataById,
  getCompanyContactListData,
} from '../companyContacts/mockData'
import { _getMOSelectItemDataById, getOrderListData } from '../orders/mockData'
import {
  _getSCTDataById,
  getSupplierContactListData,
} from '../supplierContacts/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import {
  getOpportunityListData,
  _getOSelectItemDataById,
} from './../opportunities/mockData'
import { getPriorityById, PriorityIds } from '~/services/selectLists/priorities'
import {
  getTaskStatusById,
  TaskStatusIds,
} from '~/services/selectLists/taskStatii'
import type { ITask } from '~/types/task/models'
import type { TEntityId } from '~/types/models'

const taskListData = undefined as unknown as ITask[]

export const getTaskListData = getListDataFunctionFactory<ITask[]>({
  listData: taskListData,
  storageKey: 'tasks',
  getStartingListData: getStartingTaskListData,
})

export async function getTaskDataById(
  id: TEntityId
): Promise<ITask | undefined> {
  const list = await getTaskListData()
  if (!list) {
    throw new Error(`taskList returned undefined/null`)
  }
  return list.find((task: ITask) => task.id === id)
}

export async function getTaskListDataByCompanyId(
  id: TEntityId
): Promise<ITask[]> {
  const list = await getTaskListData()
  if (!list) {
    throw new Error(`taskList returned undefined/null`)
  }
  return (
    list.filter(
      (task: ITask) =>
        task.company?.id === id || task.contact?.company?.id === id
    ) ?? []
  )
}

export async function getTaskListDataByOpportunityId(
  id: TEntityId
): Promise<ITask[]> {
  const list = await getTaskListData()
  if (!list) {
    throw new Error(`taskList returned undefined/null`)
  }
  return list.filter((task: ITask) => task.opportunity?.id === id) ?? []
}

export async function getTaskListDataByCaseId(id: TEntityId): Promise<ITask[]> {
  const list = await getTaskListData()
  if (!list) {
    throw new Error(`taskList returned undefined/null`)
  }
  return list.filter((task: ITask) => task.case?.id === id)
}

export async function getTaskListDataByOrderId(
  id: TEntityId
): Promise<ITask[]> {
  const list = await getTaskListData()
  if (!list) {
    throw new Error(`taskList returned undefined/null`)
  }
  return list.filter((task: ITask) => task.order && task.order.id === id) ?? []
}

export async function getTaskListDataByProjectId(
  id: TEntityId
): Promise<ITask[]> {
  const list = await getTaskListData()
  if (!list) {
    throw new Error(`taskList returned undefined/null`)
  }
  return (
    list.filter((task: ITask) => task.project && task.project.id === id) ?? []
  )
}

export async function getTaskListDataByLocationId(
  id: TEntityId
): Promise<ITask[]> {
  const list = await getTaskListData()
  if (!list) {
    throw new Error(`taskList returned undefined/null`)
  }
  return (
    list.filter((task: ITask) => task.location && task.location.id === id) ?? []
  )
}

export async function getTaskListDataByOwnerId(
  id: TEntityId
): Promise<ITask[]> {
  const list = await getTaskListData()
  if (!list) {
    throw new Error(`taskList returned undefined/null`)
  }
  const tasks = list.filter((t: ITask) => t.owner && t.owner.id === id) ?? []
  return tasks
}

export async function getTaskListDataBySupplierId(
  id: TEntityId
): Promise<ITask[]> {
  const list = await getTaskListData()
  if (!list) {
    throw new Error(`taskList returned undefined/null`)
  }
  const tasks =
    list.filter(
      (t: ITask) => t.supplier?.id === id || t.contact?.supplier?.id === id
    ) ?? []
  return tasks
}

export async function getTaskListDataByTsdId(id: TEntityId): Promise<ITask[]> {
  const list = await getTaskListData()
  if (!list) {
    throw new Error(`taskList returned undefined/null`)
  }
  const tasks =
    list.filter((t: ITask) => t.tsd?.id === id || t.contact?.tsd?.id === id) ??
    []
  return tasks
}

export async function getTaskListDataByTeamMemberId(
  id: TEntityId
): Promise<ITask[]> {
  const list = await getTaskListData()
  if (!list) {
    throw new Error(`taskList returned undefined/null`)
  }
  const tasks =
    list.filter((t: ITask) => t.owner?.id === id || t.assignedTo?.id === id) ??
    []
  return tasks
}

export async function getStartingTaskListData(): Promise<ITask[]> {
  const tmList = await getTeamMemberListData()
  const cList = await getCompanyListData()
  const locList = await getLocationListData()
  const sList = await getSupplierListData()
  const tList = await getTsdListData()
  const oList = await getOpportunityListData()
  const moList = await getOrderListData()

  const projList = await getProjectListData()
  const cctList = await getCompanyContactListData()
  const sctList = await getSupplierContactListData()

  return [
    {
      id: 'XXTA0001',
      hrid: 'TA0001',
      project: _getProjSelectItemDataById('XXP0002', projList),
      descr: 'Schedule DIA Install',
      notes: 'This is the first task associated with E-LAN Migration',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2023-01-02',
      completeByDate: '2023-01-15',
      completedDate: '2023-01-15',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getTaskStatusById(TaskStatusIds.IN_PROGRESS),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0002',
      hrid: 'TA0002',
      project: _getProjSelectItemDataById('XXP0002', projList),
      descr: 'Call Cogent re New Drop',
      notes: 'This is the second task associated with E-LAN Migration',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2023-02-02',
      completeByDate: '2023-02-15',
      completedDate: '2023-02-15',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getTaskStatusById(TaskStatusIds.TO_BE_DONE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0003',
      hrid: 'TA0003',
      location: _getLSelectItemDataById('XXL0001', locList),
      descr: 'Check last mile fiber',
      notes: 'This is the first task associated with this location',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2023-01-02',
      completeByDate: '2023-01-15',
      completedDate: '2023-01-15',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getTaskStatusById(TaskStatusIds.COMPLETE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0004',
      hrid: 'TA0004',
      location: _getLSelectItemDataById('XXL0001', locList),
      descr: 'Check permit status',
      notes: 'This is the second task associated with this location',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2022-02-02',
      completeByDate: '2023-02-15',
      completedDate: '2023-02-15',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getTaskStatusById(TaskStatusIds.COMPLETE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0005',
      hrid: 'TA0005',
      descr: 'Get SSN',
      notes: 'This is the first task associated with Robert Downy, Jr.',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2023-01-02',
      completeByDate: '2023-01-15',
      completedDate: '2023-01-15',
      owner: _getTMDataById('XXTM0005', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0005', tmList),
      status: getTaskStatusById(TaskStatusIds.IN_PROGRESS),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0006',
      hrid: 'TA0006',
      descr: 'Get W9',
      notes: 'This is the second task associated with Robert Downy, Jr.',
      priority: getPriorityById(PriorityIds.LOW),
      startDate: '2022-02-02',
      completeByDate: '2023-02-15',
      completedDate: '2023-02-15',
      owner: _getTMDataById('XXTM0005', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0005', tmList),
      status: getTaskStatusById(TaskStatusIds.IN_PROGRESS),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0007',
      hrid: 'TA0007',
      supplier: _getSSelectItemDataById('XXS0002', sList),
      descr: 'Verify Verizon relationship',
      notes: 'This is the first task associated with Supplier Cogent',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2023-01-02',
      completeByDate: '2023-01-15',
      completedDate: '2023-01-15',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getTaskStatusById(TaskStatusIds.TO_BE_DONE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0008',
      hrid: 'TA0008',
      supplier: _getSSelectItemDataById('XXS0002', sList),
      descr: 'Get Registration email',
      notes: 'This is the second task associated with Supplier Cogent',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2022-02-02',
      completeByDate: '2023-02-15',
      completedDate: '2023-02-15',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getTaskStatusById(TaskStatusIds.IN_PROGRESS),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0009',
      hrid: 'TA0009',
      supplier: _getSSelectItemDataById('XXS0003', sList),
      descr: 'Check Cisco availability',
      notes: 'This is the first task associated with Supplier Comcast',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2023-01-02',
      completeByDate: '2023-01-15',
      completedDate: '2023-01-15',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getTaskStatusById(TaskStatusIds.IN_PROGRESS),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0010',
      hrid: 'TA0010',
      supplier: _getSSelectItemDataById('XXS0003', sList),
      descr: 'Check mulit- vs single-mode for HBA',
      notes: 'This is the second task associated with Supplier Comcast',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2022-02-02',
      completeByDate: '2023-02-15',
      completedDate: '2023-02-15',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getTaskStatusById(TaskStatusIds.TO_BE_DONE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0011',
      hrid: 'TA0011',
      tsd: _getTSelectItemDataById('XXT0001', tList),
      descr: 'Check order status',
      notes: 'This is the first task associated with Tsd TBI',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2023-01-02',
      completeByDate: '2023-01-15',
      completedDate: '2023-01-15',
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getTaskStatusById(TaskStatusIds.IN_PROGRESS),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0012',
      hrid: 'TA0012',
      tsd: _getTSelectItemDataById('XXT0001', tList),
      descr: 'Check availability',
      notes: 'This is the second task associated with Tsd TBI',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2022-02-02',
      completeByDate: '2023-02-15',
      completedDate: '2023-02-15',
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getTaskStatusById(TaskStatusIds.IN_PROGRESS),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0013',
      hrid: 'TA0013',
      opportunity: _getOSelectItemDataById('XXO0003', oList),
      descr: 'Verify requested install date',
      notes: 'This is the first task associated with opportunity O00003',
      priority: getPriorityById(PriorityIds.HIGH),
      startDate: '2023-01-02',
      completeByDate: '2023-01-15',
      completedDate: '2023-01-15',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getTaskStatusById(TaskStatusIds.IN_PROGRESS),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0014',
      hrid: 'TA0014',
      opportunity: _getOSelectItemDataById('XXO0003', oList),
      descr: 'Needs CCaaS too?',
      notes: 'This is the second task associated with opportunity O00003',
      priority: getPriorityById(PriorityIds.LOW),
      startDate: '2022-02-02',
      completeByDate: '2023-02-15',
      completedDate: '2023-02-15',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getTaskStatusById(TaskStatusIds.IN_PROGRESS),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0015',
      hrid: 'TA0015',
      company: _getCSelectItemDataById('XXC0001', cList),
      descr: 'Verify requested install date',
      notes: 'This is the first task associated with company C00001',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2023-01-02',
      completeByDate: '2023-01-15',
      completedDate: '2023-01-15',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getTaskStatusById(TaskStatusIds.IN_PROGRESS),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0016',
      hrid: 'TA0016',
      company: _getCSelectItemDataById('XXC0001', cList),
      descr: 'Needs CCaaS too?',
      notes: 'This is the second task associated with company C00001',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2022-02-02',
      completeByDate: '2023-02-15',
      completedDate: '2023-02-15',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getTaskStatusById(TaskStatusIds.TO_BE_DONE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0017',
      hrid: 'TA0017',
      order: _getMOSelectItemDataById('XXORD0001', moList),
      descr: 'Verify availability to order',
      notes: 'This is the first task associated with order ORD0001',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2023-01-02',
      completeByDate: '2023-01-15',
      completedDate: '2023-01-15',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getTaskStatusById(TaskStatusIds.IN_PROGRESS),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTA0018',
      hrid: 'TA0018',
      order: _getMOSelectItemDataById('XXORD0001', moList),
      descr: 'Check last mile availability for Boston',
      notes: 'This is the second task associated with order ORD0001',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2022-02-02',
      completeByDate: '2023-02-15',
      completedDate: '2023-02-15',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getTaskStatusById(TaskStatusIds.TO_BE_DONE),
      version: '1',
      canBeDeleted: false,
    },
    // A case's tasks (cynque-api-server #1739). XXCA0001 already reports
    // canBeDeleted false, as a case with tasks does on the server. Its label
    // is the case's descr, which the server keeps cases.label equal to.
    {
      id: 'XXTA0019',
      hrid: 'TA0019',
      case: { id: 'XXCA0001', label: 'POTS line at Boston dropping calls' },
      descr: 'Open a trouble ticket with the carrier',
      notes: 'Ask for a line test on the Boston POTS line',
      priority: getPriorityById(PriorityIds.HIGH),
      startDate: '2022-03-01',
      completeByDate: '2022-03-02',
      owner: _getTMDataById('XXTM0006', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0006', tmList),
      status: getTaskStatusById(TaskStatusIds.IN_PROGRESS),
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXTA0020',
      hrid: 'TA0020',
      case: { id: 'XXCA0001', label: 'POTS line at Boston dropping calls' },
      descr: 'Confirm with the customer once the line is stable',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDate: '2022-03-01',
      owner: _getTMDataById('XXTM0006', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0006', tmList),
      status: getTaskStatusById(TaskStatusIds.TO_BE_DONE),
      version: '1',
      canBeDeleted: true,
    },
  ]
}
